// src/components/navbar/NotificationBell.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  CreditCard as CreditCardIcon,
  Gavel as GavelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { usePendingAuthorizations } from '../../hooks/usePendingAuthorizations';
import { useAuth } from '../../context/AuthContext';
import { AutorizacionesApi, AutorizacionPendiente } from '../../services/api/autorizacionesApi';
import api from '../../utils/axios';
import Swal from 'sweetalert2';
import { toast } from '../../utils/toast';
import './notificationBell.scss';

interface Alerta {
  Tipo: 'STOCK_BAJO' | 'COMPRA_RETRASADA' | 'CUOTA_VENCIDA' | 'VENTA_PENDIENTE' | 'PRODUCTO_VENCIDO';
  Prioridad: 'ALTA' | 'MEDIA' | 'BAJA';
  Descripcion: string;
  IdEntidad: number;
}

interface SeccionAlerta {
  tipo: Alerta['Tipo'];
  label: string;
  icon: React.ReactElement;
  color: string;
  items: Alerta[];
}

const NotificationBell: React.FC = () => {
  const theme = useTheme();
  // Colores por prioridad tomados del tema (coherencia con la paleta corporativa)
  const COLORES_PRIORIDAD: Record<string, string> = {
    ALTA: theme.palette.error.main,
    MEDIA: theme.palette.warning.main,
    BAJA: theme.palette.success.main,
  };
  const { user } = useAuth();
  const esAdminOSupervisor = user?.Rol === 'Administrador' || user?.Rol === 'Supervisor';
  const { pendientes, count, approve, reject, hasNewRequest, clearNewRequestFlag } = usePendingAuthorizations();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tab, setTab] = useState(0); // 0 = Autorizaciones, 1 = Alertas

  const open = Boolean(anchorEl);
  const totalCount = count + alertas.length;

  const loadAlertas = useCallback(async () => {
    setLoadingAlertas(true);
    try {
      const { data } = await api.get<Alerta[]>('/reportes/alertas');
      setAlertas(data.filter(a =>
        a.Tipo === 'STOCK_BAJO' || a.Tipo === 'COMPRA_RETRASADA' || a.Tipo === 'CUOTA_VENCIDA'
      ));
    } catch {
      // silencioso
    } finally {
      setLoadingAlertas(false);
    }
  }, []);

  // Polling de alertas con pausa cuando la pestaña esta oculta (ahorra requests)
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (!interval) { loadAlertas(); interval = setInterval(loadAlertas, 60000); } };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVisibility = () => (document.hidden ? stop() : start());

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [loadAlertas]);

  // Aviso NO bloqueante al llegar nueva solicitud (antes era un Swal modal que
  // interrumpia al usuario). Solo Admin/Supervisor y con el menu cerrado.
  useEffect(() => {
    if (hasNewRequest && esAdminOSupervisor && !open) {
      const nombre = pendientes[0]?.accion?.nombre || 'Autorización';
      const solicitante = pendientes[0]?.solicitante?.nombre || 'Usuario';
      toast.info(`Nueva solicitud: ${nombre} — por ${solicitante}`, { autoHideDuration: 6000 });
      clearNewRequestFlag();
    }
  }, [hasNewRequest, esAdminOSupervisor, open, pendientes, clearNewRequestFlag]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    clearNewRequestFlag();
    // Abrir en la pestaña con contenido relevante
    setTab(pendientes.length > 0 ? 0 : alertas.length > 0 ? 1 : 0);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleAprobar = async (codigo: string) => {
    const { value: obs } = await Swal.fire({
      title: 'Aprobar autorización',
      input: 'textarea',
      inputPlaceholder: 'Observaciones (opcional)...',
      showCancelButton: true,
      confirmButtonText: 'Aprobar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.success.main,
    });
    if (obs === false) return;
    try {
      setActionLoading(codigo);
      await approve(codigo, obs);
      toast.success('Autorización aprobada');
    } catch {
      toast.error('No se pudo aprobar la autorización');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRechazar = async (codigo: string) => {
    const { value: obs } = await Swal.fire({
      title: 'Rechazar autorización',
      input: 'textarea',
      inputPlaceholder: 'Motivo del rechazo...',
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.error.main,
    });
    if (obs === false) return;
    try {
      setActionLoading(codigo);
      await reject(codigo, obs);
      toast.success('Autorización rechazada');
    } catch {
      toast.error('No se pudo rechazar la autorización');
    } finally {
      setActionLoading(null);
    }
  };

  const getTiempoColor = (tiempo: string) => {
    if (tiempo === 'Expirado') return 'error';
    if (tiempo.includes('minutos')) {
      const mins = parseInt(tiempo);
      if (mins <= 5) return 'error';
      if (mins <= 15) return 'warning';
    }
    return 'success';
  };

  const secciones: SeccionAlerta[] = useMemo(() => ([
    {
      tipo: 'CUOTA_VENCIDA' as const,
      label: 'Cuotas vencidas',
      icon: <CreditCardIcon fontSize="small" />,
      color: theme.palette.error.main,
      items: alertas.filter(a => a.Tipo === 'CUOTA_VENCIDA'),
    },
    {
      tipo: 'STOCK_BAJO' as const,
      label: 'Stock crítico',
      icon: <InventoryIcon fontSize="small" />,
      color: theme.palette.warning.main,
      items: alertas.filter(a => a.Tipo === 'STOCK_BAJO'),
    },
    {
      tipo: 'COMPRA_RETRASADA' as const,
      label: 'Compras vencidas',
      icon: <ShoppingCartIcon fontSize="small" />,
      color: theme.palette.info.main,
      items: alertas.filter(a => a.Tipo === 'COMPRA_RETRASADA'),
    },
  ] as SeccionAlerta[]).filter(s => s.items.length > 0), [alertas, theme]);

  return (
    <>
      <IconButton color="inherit" onClick={handleClick} className="notification-bell">
        <Badge badgeContent={totalCount || undefined} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="notification-menu"
        PaperProps={{ sx: { maxHeight: 560, width: 420, mt: 1, borderRadius: 2, overflow: 'hidden' } }}
      >
        {/* Encabezado */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={800}>Notificaciones</Typography>
          {totalCount > 0 && (
            <Chip label={`${totalCount} nuevas`} size="small" color="error" sx={{ height: 22, fontWeight: 700, fontSize: '0.7rem' }} />
          )}
        </Box>

        {/* Pestañas */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' },
          }}
        >
          <Tab
            icon={<GavelIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={`Autorizaciones${pendientes.length ? ` (${pendientes.length})` : ''}`}
          />
          <Tab
            icon={<NotificationsIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={`Alertas${alertas.length ? ` (${alertas.length})` : ''}`}
          />
        </Tabs>

        {/* Contenido con scroll */}
        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {/* Tab 0: Autorizaciones */}
          {tab === 0 && (
            pendientes.length === 0 ? (
              <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
                <Typography variant="body2" fontWeight={600}>Sin autorizaciones pendientes</Typography>
                <Typography variant="caption" color="text.secondary">Las nuevas solicitudes aparecerán aquí.</Typography>
              </Box>
            ) : (
              pendientes.map((auth) => (
                <Box key={auth.id} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', transition: 'background-color .15s', '&:hover': { bgcolor: 'action.hover' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.25 }}>{auth.accion.nombre}</Typography>
                    <Chip label={auth.accion.modulo} size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                    Solicitado por <strong>{auth.solicitante.nombre}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: `${getTiempoColor(auth.tiempoRestante)}.main` }} />
                    <Typography variant="caption" fontWeight={600} color={`${getTiempoColor(auth.tiempoRestante)}.main`}>
                      {auth.tiempoRestante}
                    </Typography>
                    {auth.datosAccion?.codigoFactura && (
                      <Typography variant="caption" sx={{ ml: 'auto', fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.2, borderRadius: 1 }}>
                        {auth.datosAccion.codigoFactura}
                      </Typography>
                    )}
                  </Box>
                  {esAdminOSupervisor && actionLoading !== auth.codigo && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                      <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />}
                        onClick={() => handleAprobar(auth.codigo)}
                        sx={{ fontSize: '0.72rem', py: 0.3, px: 1.5 }}>
                        Aprobar
                      </Button>
                      <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />}
                        onClick={() => handleRechazar(auth.codigo)}
                        sx={{ fontSize: '0.72rem', py: 0.3, px: 1.5 }}>
                        Rechazar
                      </Button>
                    </Stack>
                  )}
                  {actionLoading === auth.codigo && (
                    <CircularProgress size={16} sx={{ mt: 1 }} />
                  )}
                </Box>
              ))
            )
          )}

          {/* Tab 1: Alertas */}
          {tab === 1 && (
            loadingAlertas ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={22} />
              </Box>
            ) : secciones.length === 0 ? (
              <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
                <Typography variant="body2" fontWeight={600}>Todo en orden</Typography>
                <Typography variant="caption" color="text.secondary">No hay alertas de stock, cuotas ni compras.</Typography>
              </Box>
            ) : (
              secciones.map((seccion) => (
                <React.Fragment key={seccion.tipo}>
                  <Box sx={{ px: 2, py: 0.9, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'action.hover' }}>
                    <Box sx={{ color: seccion.color, display: 'flex' }}>{seccion.icon}</Box>
                    <Typography variant="caption" fontWeight={800} sx={{ color: seccion.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {seccion.label} ({seccion.items.length})
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {seccion.items.slice(0, 4).map((a, i) => (
                      <ListItem key={i} sx={{ px: 2, py: 0.7, '&:hover': { bgcolor: 'action.hover' } }}>
                        <ListItemIcon sx={{ minWidth: 26 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORES_PRIORIDAD[a.Prioridad] || theme.palette.text.disabled }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="caption" sx={{ lineHeight: 1.3 }}>{a.Descripcion}</Typography>} />
                      </ListItem>
                    ))}
                    {seccion.items.length > 4 && (
                      <ListItem sx={{ px: 2, py: 0.3 }}>
                        <Typography variant="caption" color="text.secondary">+{seccion.items.length - 4} más…</Typography>
                      </ListItem>
                    )}
                  </List>
                </React.Fragment>
              ))
            )
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
